import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from './api';

// Renders the candlestick+volume SVG produced by the shared chart-service
// (see /Users/edward/docker/chart-service) via screener-backend's
// /api/screener/chartdirector-chart proxy, and draws a lightweight crosshair
// + OHLC tooltip on hover using the plot-bounds metadata and candle list
// returned alongside the SVG. Click-to-pick-price (used by OrderPanel for
// entry/stop price) is unchanged from before.
const ChartDirectorChart = ({
  market, dataset = 'av', live = false, symbol, date, period, pickMode, onPricePick, transactionMarkers = [],
  fullHistory = false,
  reviewEndDate = '',
  reviewStartDate = '',
}) => {
  const [failed, setFailed] = useState(false);
  const [svg, setSvg] = useState('');
  const [plot, setPlot] = useState(null);
  const [candles, setCandles] = useState([]);
  const [hover, setHover] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const requestUrl = useMemo(() => {
    if (!market || !symbol || !date) return '';
    return `/api/screener/chartdirector-chart/${market}/${encodeURIComponent(symbol)}?date=${encodeURIComponent(date)}&period=${period}&dataset=${dataset}${live ? '&live=1' : ''}${fullHistory ? '&full=1' : ''}${reviewEndDate ? `&reviewEnd=${reviewEndDate}` : ''}${reviewStartDate ? `&reviewStart=${reviewStartDate}` : ''}`;
  }, [market, symbol, date, period, dataset, live, fullHistory, reviewEndDate, reviewStartDate]);

  // Refresh only live charts. This keeps the supplied ChartDirector image in
  // sync with the minute-by-minute `us_screens` Finviz candle.
  useEffect(() => {
    if (!live) return undefined;
    const refreshId = window.setInterval(() => setRefreshTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(refreshId);
  }, [live, symbol, date, period, dataset]);

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    setSvg('');
    setPlot(null);
    setCandles([]);
    setHover(null);
    if (!requestUrl) return () => controller.abort();

    const load = async () => {
      try {
        const response = await apiFetch(requestUrl, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to load ChartDirector chart');
        const body = await response.json();
        if (!body?.plot || !body?.svg) throw new Error('Chart price scale is unavailable');
        setSvg(body.svg);
        setPlot(body.plot);
        setCandles(Array.isArray(body.candles) ? body.candles : []);
      } catch (error) {
        if (error.name !== 'AbortError') setFailed(true);
      }
    };

    load();
    return () => controller.abort();
  }, [requestUrl, refreshTick]);

  const imageSrc = useMemo(() => {
    if (!svg) return '';
    const fittedSvg = svg.replace('<svg ', '<svg preserveAspectRatio="none" ');
    const blob = new Blob([fittedSvg], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
  }, [svg]);

  const svgSize = useMemo(() => {
    const viewBox = svg.match(/viewBox=["']\s*[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+([\d.]+)["']/i);
    if (viewBox) return { width: Number(viewBox[1]), height: Number(viewBox[2]) };
    const width = svg.match(/<svg[^>]*\swidth=["']([\d.]+)/i);
    const height = svg.match(/<svg[^>]*\sheight=["']([\d.]+)/i);
    return { width: Number(width?.[1]), height: Number(height?.[1]) };
  }, [svg]);

  const markerStyle = (marker) => {
    const numericPrice = Number(marker.price);
    if (!plot || !(svgSize.width > 0) || !(svgSize.height > 0)
        || !(numericPrice > 0) || numericPrice < plot.minPrice || numericPrice > plot.maxPrice
        || candles.length === 0) return null;
    const markerDate = String(marker.date || '').slice(0, 10);
    const candleIndex = candles.findIndex((candle) => String(candle.time).slice(0, 10) === markerDate);
    if (candleIndex < 0) return null;
    const x = plot.left + (candleIndex / Math.max(1, candles.length - 1)) * plot.width;
    const y = plot.top + ((plot.maxPrice - numericPrice) / (plot.maxPrice - plot.minPrice)) * plot.height;
    return {
      left: `${(x / svgSize.width) * 100}%`,
      top: `${(y / svgSize.height) * 100}%`,
    };
  };

  useEffect(() => () => { if (imageSrc) URL.revokeObjectURL(imageSrc); }, [imageSrc]);

  const toChartCoords = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const naturalWidth = event.currentTarget.naturalWidth;
    const naturalHeight = event.currentTarget.naturalHeight;
    if (!rect.width || !rect.height || !naturalWidth || !naturalHeight) return null;
    return {
      x: (event.clientX - rect.left) * naturalWidth / rect.width,
      y: (event.clientY - rect.top) * naturalHeight / rect.height,
      naturalWidth,
    };
  };

  const handleClick = (event) => {
    if (!pickMode || !onPricePick || !plot) return;
    const coords = toChartCoords(event);
    if (!coords) return;
    const { x: chartX, y: chartY } = coords;
    if (chartX < plot.left || chartX > plot.left + plot.width
        || chartY < plot.top || chartY > plot.top + plot.height) return;

    const price = plot.maxPrice - ((chartY - plot.top) / plot.height) * (plot.maxPrice - plot.minPrice);
    onPricePick(pickMode, Number(price.toFixed(4)));
  };

  const handleMouseMove = (event) => {
    if (!plot || candles.length === 0) return;
    const coords = toChartCoords(event);
    if (!coords) return;
    const { x: chartX, naturalWidth } = coords;
    if (chartX < plot.left || chartX > plot.left + plot.width) return setHover(null);

    const idx = Math.round(((chartX - plot.left) / plot.width) * (candles.length - 1));
    const candle = candles[idx];
    if (!candle) return setHover(null);

    const lineX = plot.left + (idx / Math.max(1, candles.length - 1)) * plot.width;
    setHover({ candle, lineXPct: (lineX / naturalWidth) * 100 });
  };

  return (
    <div className="chartdirector-chart-shell">
      {imageSrc && !failed && (
        <img
          className={`chartdirector-chart-image ${pickMode ? 'price-picking' : ''}`}
          src={imageSrc}
          alt=""
          draggable="false"
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHover(null)}
          onError={() => setFailed(true)}
        />
      )}
      {transactionMarkers.map((marker, index) => {
        const style = markerStyle(marker);
        return style ? (
          <div
            key={`${marker.kind}-${marker.date}-${marker.price}-${index}`}
            className={`chartdirector-transaction-marker ${marker.kind || ''}`}
            style={style}
          >
            <i />
            <span>{marker.label} {Number(marker.price).toFixed(2)}</span>
          </div>
        ) : null;
      })}
      {hover && (
        <>
          <div className="chartdirector-crosshair" style={{ left: `${hover.lineXPct}%` }} />
          <div className="chartdirector-tooltip" style={{ left: `${hover.lineXPct}%` }}>
            <div>{hover.candle.time}</div>
            <div>O {hover.candle.open} H {hover.candle.high}</div>
            <div>L {hover.candle.low} C {hover.candle.close}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChartDirectorChart;
